import { useState, useCallback, useMemo, useEffect } from 'react';

interface SliceData {
  id: string;
  label: string;
  icon: string;
  color: string;
  startAngle: number;
  endAngle: number;
}

const sliceConfigs = [
  { id: 'worship',  label: 'Worship',      icon: 'ri-heart-3-line',    color: '#D4946C' },
  { id: 'vocals',   label: 'Vocals',       icon: 'ri-mic-line',       color: '#C8845A' },
  { id: 'theatre',  label: 'Theatre',      icon: 'ri-movie-line',     color: '#D09064' },
  { id: 'guitar',   label: 'Guitar',       icon: 'ri-music-2-line',   color: '#CC8858' },
  { id: 'piano',    label: 'Piano',        icon: 'ri-music-line',     color: '#C48050' },
  { id: 'violin',   label: 'Violin',       icon: 'ri-music-2-line',   color: '#D89C70' },
  { id: 'compositions', label: 'Compositions', icon: 'ri-quill-pen-line', color: '#C88C5E' },
];

const HOVER_COLOR = '#E9762B';
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const TRANSITION_DURATION = '0.4s';

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function buildDonutPath(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  startAngle: number,
  endAngle: number,
): string {
  const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
  const outerEnd   = polarToCartesian(cx, cy, outerR, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, startAngle);
  const innerEnd   = polarToCartesian(cx, cy, innerR, endAngle);

  const sweep = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${sweep} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerR} ${innerR} 0 ${sweep} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

function computeSlices(): SliceData[] {
  const total = sliceConfigs.length;
  const anglePer = 360 / total;
  return sliceConfigs.map((s, i) => ({
    ...s,
    startAngle: i * anglePer,
    endAngle: (i + 1) * anglePer,
  }));
}

export default function MediaPieChart() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const slices = useMemo(() => computeSlices(), []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSliceHover = useCallback((sliceId: string | null) => {
    setHoveredId(sliceId);
  }, []);

  const isMobile = windowWidth < 768;
  const size = isMobile ? 340 : 580;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = isMobile ? 140 : 240;
  const innerR = isMobile ? 72 : 126;
  const hoverExpand = isMobile ? 5 : 8;

  const hoveredSlice = hoveredId ? slices.find((s) => s.id === hoveredId) : null;

  return (
    <div className="flex justify-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ overflow: 'visible' }}
        >
          {slices.map((slice) => {
            const isHovered = hoveredId === slice.id;
            const isAnyHovered = hoveredId !== null;
            const expandedR = isHovered ? outerR + hoverExpand : outerR;

            const pathD = buildDonutPath(cx, cy, expandedR, innerR, slice.startAngle, slice.endAngle);

            const midAngle = (slice.startAngle + slice.endAngle) / 2;
            const labelR = innerR + (outerR - innerR) * 0.56;
            const labelPos = polarToCartesian(cx, cy, labelR, midAngle);

            const fillColor = isHovered ? HOVER_COLOR : slice.color;
            const sliceOpacity = isAnyHovered ? (isHovered ? 1 : 0.38) : 0.90;

            return (
              <g key={slice.id}>
                <path
                  d={pathD}
                  fill={fillColor}
                  opacity={sliceOpacity}
                  style={{
                    transition: `opacity ${TRANSITION_DURATION} ${EASING}, fill ${TRANSITION_DURATION} ${EASING}, filter ${TRANSITION_DURATION} ${EASING}`,
                    filter: isHovered
                      ? 'brightness(1.06)'
                      : 'none',
                  }}
                  onMouseEnter={() => handleSliceHover(slice.id)}
                  onMouseLeave={() => handleSliceHover(null)}
                />
                <text
                  x={labelPos.x}
                  y={labelPos.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#1A1A1A"
                  fontSize={isMobile ? 11 : 14}
                  fontWeight="650"
                  fontFamily="Inter, system-ui, sans-serif"
                  letterSpacing="0.02em"
                  className="pointer-events-none select-none"
                  style={{
                    transition: `font-size ${TRANSITION_DURATION} ${EASING}`,
                  }}
                >
                  {slice.label}
                </text>
              </g>
            );
          })}

          {/* Center circle */}
          <defs>
            <radialGradient id="centerGradPie" cx="50%" cy="40%" r="50%">
              <stop offset="0%" stopColor="oklch(var(--background-50))" />
              <stop offset="100%" stopColor="oklch(var(--background-200))" />
            </radialGradient>
          </defs>
          <circle cx={cx} cy={cy} r={innerR - 2} fill="url(#centerGradPie)" />
          <circle
            cx={cx}
            cy={cy}
            r={innerR - 2}
            fill="none"
            stroke="oklch(var(--background-300) / 0.3)"
            strokeWidth="1"
          />

          {/* Center content */}
          <foreignObject x={cx - 44} y={cy - 44} width={88} height={88}>
            <div className="w-full h-full flex flex-col items-center justify-center text-center pointer-events-none select-none">
              {hoveredSlice ? (
                <>
                  <i
                    className={`${hoveredSlice.icon} text-xl leading-none`}
                    style={{ color: HOVER_COLOR }}
                  />
                  <span
                    className="font-label text-[9px] font-semibold mt-0.5 leading-tight whitespace-nowrap"
                    style={{ color: '#1A1A1A' }}
                  >
                    {hoveredSlice.label}
                  </span>
                </>
              ) : (
                <i className="ri-music-fill text-xl leading-none" style={{ color: '#E9762B' }} />
              )}
            </div>
          </foreignObject>
        </svg>
      </div>
    </div>
  );
}
